import { useId } from 'react';
import { Plus, Minus } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Button } from '@shared/components/ui/button';

type Props = {
  label: string;
  /** Raw typed value, to let the operator type freely */
  value: string;
  onChange: (value: string) => void;
  /** Lowest value reachable with the « - » button */
  min?: number;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
};

/** Numeric field framed by large « - » / « + » buttons, sized for touch terminals. */
export const QuantityStepper = ({
  label,
  value,
  onChange,
  min = 1,
  error = false,
  helperText,
  disabled = false,
  autoFocus = false,
}: Props) => {
  const id = useId();
  const quantity = Number(value) || 0;

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Button
          aria-label="Diminuer la quantité"
          className="size-14 shrink-0"
          disabled={quantity <= min || disabled}
          onClick={() => onChange(String(quantity - 1))}
          size="icon"
          type="button"
          variant="outline"
        >
          <Minus />
        </Button>
        <Input
          aria-invalid={error || undefined}
          autoFocus={autoFocus}
          className="text-center"
          disabled={disabled}
          enterKeyHint="done"
          id={id}
          inputMode="numeric"
          onChange={(event) => onChange(event.target.value)}
          onFocus={(event) => event.target.select()}
          pattern="[0-9]*"
          size="xxl"
          value={value}
        />
        <Button
          aria-label="Augmenter la quantité"
          className="size-14 shrink-0"
          disabled={disabled}
          onClick={() => onChange(String(Math.max(quantity, min - 1) + 1))}
          size="icon"
          type="button"
          variant="outline"
        >
          <Plus />
        </Button>
      </div>
      {helperText && (
        <p className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>{helperText}</p>
      )}
    </div>
  );
};
